import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Lock, ArrowRight, Plus, Sparkles, ShieldCheck } from "lucide-react";
import { CHECKOUT } from "@/constants/testIds";
import { usePricing, fmtPrice as fmt } from "@/hooks/usePricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const includes = [
  "Manuale completo ComprehensionUp (PDF, 31 pagine)",
  "Il processo pratico in 4 fasi",
  "Esempi guidati per Medicina, Diritto ed Economia",
  "Esercizi guidati da usare sui tuoi testi",
  "Download immediato e accesso a vita",
];

export default function Checkout() {
  const pricing = usePricing();
  const MAIN_PRICE = pricing.main.price;
  const MAIN_COMPARE = pricing.main.compare_at;
  const UPSELL_PRICE = pricing.upsell.price;
  const UPSELL_COMPARE = pricing.upsell.compare_at;

  const [addUpsell, setAddUpsell] = useState(false);
  const [loading, setLoading] = useState(false);

  const total = MAIN_PRICE + (addUpsell ? UPSELL_PRICE : 0);
  const compareTotal = (MAIN_COMPARE || MAIN_PRICE) + (addUpsell ? (UPSELL_COMPARE || UPSELL_PRICE) : 0);
  const discountPct = MAIN_COMPARE ? Math.round((1 - MAIN_PRICE / MAIN_COMPARE) * 100) : 0;

  const goToCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/shopify/checkout`, { params: { add_upsell: addUpsell } });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Impossibile aprire il checkout. Riprova tra poco.");
      setLoading(false);
    }
  };

  return (
    <section id="checkout" data-testid={CHECKOUT.section} className="relative py-16 sm:py-24 lg:py-28">
      <div className="absolute inset-0 ps-grid-bg opacity-60" />
      <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5982c5]">Inizia oggi</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1f2937]">
            Il tuo prossimo livello parte da qui
          </h2>
        </div>

        <div className="bg-white rounded-[2rem] shadow-[0_30px_60px_rgb(89,130,197,0.15)] border border-[#e5e7eb] overflow-hidden grid md:grid-cols-2">
          {/* Left: value */}
          <div className="bg-[#5982c5] text-white p-8 sm:p-10 flex flex-col">
            <span className="inline-flex w-fit items-center gap-2 bg-[#f0d203] text-[#1a1a1a] text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full">
              Offerta di lancio -{discountPct}%
            </span>
            <h3 className="mt-5 font-heading text-2xl font-bold">ComprehensionUp</h3>
            <p className="mt-1 text-white/80">Il processo pratico per capire davvero ciò che studi.</p>

            <div className="mt-6 flex items-end gap-2.5">
              <span className="font-heading text-5xl font-extrabold">{fmt(MAIN_PRICE)}</span>
              <span className="mb-1 text-lg font-semibold text-white/80 line-through decoration-2">{fmt(MAIN_COMPARE)}</span>
            </div>
            <p className="text-sm text-white/80">Pagamento unico · risparmi il {discountPct}%</p>

            <ul className="mt-8 space-y-3">
              {includes.map((it, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[#f0d203] flex items-center justify-center">
                    <Check size={13} className="text-[#1a1a1a]" strokeWidth={3} />
                  </span>
                  <span className="text-white/90 text-sm leading-relaxed">{it}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: order + Shopify checkout */}
          <div className="p-8 sm:p-10 flex flex-col justify-center">
            {/* Upsell add-on */}
            <button
              type="button"
              data-testid={CHECKOUT.upsellToggle}
              aria-pressed={addUpsell}
              onClick={() => setAddUpsell((v) => !v)}
              className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                addUpsell ? "border-[#5982c5] bg-[#5982c5]/5" : "border-dashed border-[#d1d5db] hover:border-[#5982c5]/60"
              }`}
            >
              <div className="flex items-center gap-3">
                <img src="/upsell-cover.jpg" alt="50 Prompt per Comprendere Meglio" className="w-12 h-16 object-cover rounded-md border border-[#e5e7eb] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={14} className="text-[#f0d203]" />
                    <p className="text-[10px] font-bold uppercase tracking-wide text-[#5982c5]">Aggiungi e risparmia</p>
                  </div>
                  <p className="font-heading font-bold text-[#1f2937] leading-tight text-sm">50 Prompt per Comprendere Meglio</p>
                  <p className="text-xs text-[#4b5563]">Usa l'AI per studiare meglio ·
                    <span className="font-bold text-[#1f2937]"> {fmt(UPSELL_PRICE)}</span>
                    <span className="line-through text-[#64748b] font-medium"> {fmt(UPSELL_COMPARE)}</span>
                  </p>
                </div>
                <span className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center border-2 ${addUpsell ? "bg-[#5982c5] border-[#5982c5]" : "border-[#d1d5db]"}`}>
                  {addUpsell ? <Check size={15} className="text-white" strokeWidth={3} /> : <Plus size={15} className="text-[#9ca3af]" />}
                </span>
              </div>
            </button>

            {/* Order summary */}
            <div className="mt-5 rounded-2xl bg-[#ecfef7] p-4 space-y-2">
              <div className="flex justify-between text-sm text-[#4b5563]">
                <span>ComprehensionUp</span>
                <span>
                  <span className="text-[#64748b] line-through mr-1.5">{fmt(MAIN_COMPARE)}</span>
                  <span className="font-semibold text-[#1f2937]">{fmt(MAIN_PRICE)}</span>
                </span>
              </div>
              {addUpsell && (
                <div className="flex justify-between text-sm text-[#4b5563]">
                  <span>50 Prompt per Comprendere Meglio</span>
                  <span>
                    <span className="text-[#64748b] line-through mr-1.5">{fmt(UPSELL_COMPARE)}</span>
                    <span className="font-semibold text-[#1f2937]">{fmt(UPSELL_PRICE)}</span>
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-[#5982c5]/15">
                <span className="font-heading font-bold text-[#1f2937]">Totale</span>
                <span className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold text-[#64748b] line-through">{fmt(compareTotal)}</span>
                  <span data-testid="checkout-total" className="font-heading font-extrabold text-xl text-[#5982c5]">{fmt(total)}</span>
                </span>
              </div>
            </div>

            <button
              data-testid={CHECKOUT.submit}
              onClick={goToCheckout}
              disabled={loading}
              className="mt-5 w-full bg-[#f0d203] text-[#1a1a1a] hover:bg-[#dcb902] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 font-bold px-8 py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-70 disabled:hover:translate-y-0"
            >
              {loading ? (
                <><Loader2 size={20} className="animate-spin" /> Ti porto al pagamento…</>
              ) : (
                <>Vai al pagamento sicuro · {fmt(total)} <ArrowRight size={20} /></>
              )}
            </button>

            <div className="mt-3 flex flex-col items-center gap-1 text-xs text-[#9ca3af]">
              <p className="flex items-center gap-2"><Lock size={14} /> Pagamento gestito da Shopify</p>
              <p className="flex items-center gap-2"><ShieldCheck size={14} /> Download immediato dopo l'acquisto</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
